import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Grid,
  Typography,
  InputBase,
  Fade,
  Card,
  Box,
  Tooltip,
  Chip,
} from "@material-ui/core";
import {
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Message as MessageIcon,
} from "@material-ui/icons";
import { green, red, blue, grey } from "@material-ui/core/colors";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newQuickmessages = [];

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex(
          (u) => u.id === quickemessage.id
        );
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...state, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);

    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    } else {
      return [quickemessage, ...state];
    }
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: theme.spacing(1),
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
  },
  searchContainer: {
    background: theme.palette.background.default,
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  searchInput: {
    flex: 1,
    padding: theme.spacing(0.5),
    fontSize: '0.875rem',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: theme.palette.text.secondary,
    },
  },
  tableContainer: {
    flex: 1,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: "0 8px",
  },
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)',
    },
  },
  tableCell: {
    borderBottom: "none",
    padding: theme.spacing(2),
    fontSize: "0.875rem",
    '&:first-child': {
      borderTopLeftRadius: theme.spacing(1),
      borderBottomLeftRadius: theme.spacing(1),
    },
    '&:last-child': {
      borderTopRightRadius: theme.spacing(1),
      borderBottomRightRadius: theme.spacing(1),
    },
  },
  headerCell: {
    borderBottom: "none",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 2),
    backgroundColor: 'transparent',
  },
  messageContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    maxWidth: '500px',
  },
  messageIcon: {
    color: blue[500],
    fontSize: 20,
  },
  shortcode: {
    backgroundColor: blue[50],
    color: blue[700],
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    fontSize: "0.75rem",
    fontFamily: "monospace",
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  actionButton: {
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  addButton: {
    padding: theme.spacing(1.5, 3),
    boxShadow: 'none',
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.getContrastText(theme.palette.background.default),
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.grey[900],
      color: '#fff',
      boxShadow: '0 4px 12px 0 rgba(0,0,0,0.1)',
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .MuiChip-root': {
      height: 24,
    },
  },
}));

const Quickemessages = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);

    const onCompanyQuickMessage = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };

    socket.on(`company-${user.companyId}-quickemessage`, onCompanyQuickMessage);
    return () => {
      socket.off(`company-${user.companyId}-quickemessage`, onCompanyQuickMessage);
    };
  }, [socketManager, user]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages/list", {
        params: { companyId: user.companyId, userId: user.id },
      });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success("Respuesta rápida eliminada exitosamente");
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingQuickemessage && `¿Eliminar respuesta rápida ${deletingQuickemessage.shortcode}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        ¿Está seguro? Esta acción no se puede deshacer.
      </ConfirmationModal>
      
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />

      <MainHeader>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Title className={classes.title}>
              Respostas Rápidas
              <Chip
                label={quickemessages.length}
                size="small"
                style={{
                  marginLeft: 8,
                  backgroundColor: grey[100],
                  color: grey[800],
                  fontWeight: 600,
                }}
              />
            </Title>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleOpenQuickMessageDialog}
              className={classes.addButton}
              startIcon={<AddIcon />}
            >
              Nova Resposta Rápida
            </Button>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <Box className={classes.searchContainer}>
          <SearchIcon color="action" />
          <InputBase
            className={classes.searchInput}
            placeholder="Buscar resposta rápida..."
            onChange={handleSearch}
            value={searchParam}
            fullWidth
          />
        </Box>

        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Atalho</TableCell>
                <TableCell className={classes.headerCell}>Mensagem</TableCell>
                <TableCell className={classes.headerCell} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={3} />
              ) : (
                quickemessages.map((quickemessage) => (
                  <TableRow key={quickemessage.id} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      <span className={classes.shortcode}>
                        {quickemessage.shortcode}
                      </span>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <div className={classes.messageContainer}>
                        <MessageIcon className={classes.messageIcon} />
                        <Typography variant="body2" noWrap>
                          {quickemessage.message}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      <Tooltip title="Editar" arrow>
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          onClick={() => handleEditQuickemessage(quickemessage)}
                        >
                          <EditIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir" arrow>
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingQuickemessage(quickemessage);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </MainContainer>
  );
};

export default Quickemessages;